import React, { useState, useCallback, useEffect, useMemo } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Typography, Box, Paper, Stack, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, BoxStyle, BackArrowStyle } from "services/constants";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { EPFReportTable } from "./EPFReportTable";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { ArrowBackIos } from "@mui/icons-material";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { FilterSection } from "pages/FilterContext/FilterRow";
import { EPFReportKeys } from "pages/PayRoll/PayRollHeaders";
import { useSaveView } from "components/CustomComponents/useSaveView";

export const EPFReport = ({ setShowReport }) => {
  const {
    domain,
    token,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [companyId, setCompanyId] = useState(0);
  const [PFDetails, setPFDetails] = useState([]);
  const [paySheduleDetails, setPayScheduleDetails] = useState(true);
  const [epfReportDetailsApi, setepfReportDetailsApi] = useState(false);
  const [getPayScheduleDetails, setGetPayScheduleDetails] = useState([]);
  const [payScheduleName, setPayScheduleName] = useState("");
  const [listViewPage, setListViewPage] = useState(0);
  const [empEPFData, setEmpEPFData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loadingPayScheduleDetails, setLoadingPayScheduleDetails] =
    useState(false);
  const {
    result: payShedule,
    loading,
    setLoad: reCallApi,
  } = useFetch(
    companyId !== 0
      ? `${domain}payschedule-report/?company=${companyId}&company_id=${topbarCompanyId}`
      : null,
  );
  const payrollReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  const subModuleId = payrollReports?.submodules?.find(
    (sub) => sub.sub_module_name === "EPF Report",
  )?.sub_module_id;

  // This is a read-only report screen -- there is no "edit" action here, only
  // filter/sort/download/save-view. Gating those on editor permission (always
  // false in the live provisioning payload for every Reports submodule)
  // blocked them for every user; viewer permission is the correct axis, and
  // it's already guaranteed true here since the Reports list only lets you
  // open a report you have viewer access to (see PayrollReports/index.js).
  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "EPF Report",
  );

  const handleSavedViewChange = useCallback(() => {
    setListViewPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "epf_report_saved_views",
    fullHeaders: EPFReportKeys,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  useEffect(() => {
    if (topbarCompanyId) {
      setCompanyId(topbarCompanyId);
      reCallApi(true);
      setTimeout(function () {
        setPayScheduleDetails(true);
      }, 100);
    }
  }, [reCallApi, topbarCompanyId]);

  useEffect(() => {
    if (payShedule.length !== 0 && paySheduleDetails && !loading) {
      setGetPayScheduleDetails(payShedule[0] ? payShedule[0] : []);
      setPayScheduleName(payShedule[0] ? payShedule[0]?.name : "");
      setPayScheduleDetails(false);
      if (payShedule[0]) {
        setepfReportDetailsApi(true);
        setLoadingPayScheduleDetails(true);
      }
    } else if (payShedule.length === 0) {
      setGetPayScheduleDetails([]);
      setPayScheduleName("");
      setPFDetails([]);
    }
  }, [loading, payShedule, paySheduleDetails]);

  useEffect(() => {
    const getEPFReport = async () => {
      const res = await customGetAPI(
        `${domain}pay-schedule/detailed/pf-report/?pay_schedule_id=${getPayScheduleDetails?.id}&company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setPFDetails(res?.responseDetails?.employee_details);
        setLoadingPayScheduleDetails(false);
      } else {
        setPFDetails([]);
        setLoadingPayScheduleDetails(false);
      }
    };
    if (epfReportDetailsApi) {
      getEPFReport();
      setepfReportDetailsApi(false);
    }
  }, [domain, epfReportDetailsApi, getPayScheduleDetails?.id, token, topbarCompanyId]);

  const handlePaySchedule = (event, data) => {
    if (data) {
      setGetPayScheduleDetails(data);
      setPayScheduleName(data.name);
      setepfReportDetailsApi(true);
      setLoadingPayScheduleDetails(true);
    }
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setEmpEPFData(filteredData);
      if (listViewPage * rowsPerPage >= filteredData.length) setListViewPage(0);
    },
    [listViewPage, rowsPerPage],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Pay Schedule Name": payScheduleName || "N/A",
      "Report Generated On": new Date().toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    }),
    [payScheduleName, topbarCompanyName],
  );

  return (
    <ThemeProvider theme={theme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={BoxStyle}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <IconButton
              onClick={() => setShowReport(false)}
              sx={BackArrowStyle}
            >
              <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
            </IconButton>

            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}
              >
                Employees' Provident Fund (EPF)
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Employees' Provident Fund (EPF) Report provides a detailed
                overview of the contributions made by both employees and
                employers towards the EPF scheme.
              </Typography>
            </Box>
          </Stack>
          <FilterSection
            filterLabel="Filters"
            filters={[
              {
                width: 480,
                props: {
                  dataList: payShedule.map((obj) => ({
                    ...obj,
                    label: obj.name,
                  })),
                  selectedValue: payScheduleName,
                  updateDataFunction: handlePaySchedule,
                  Icons: PersonOutlineIcon,
                  staticLabel: "PaySchedule",
                },
              },
            ]}
          />
          <Paper sx={{ width: "100%", height: "76vh", mt: 2 }}>
            <CustomFilter
              title="EPF"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={PFDetails}
              filteredData={empEPFData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              newButtonComponent={newButtonComponent}
            />

            <Mui.Grid sx={{ ml: 2, mr: 2 }}>
              {loadingPayScheduleDetails ? (
                <Loader />
              ) : (
                <EPFReportTable
                  dataLength={PFDetails}
                  PFDetails={empEPFData}
                  listViewPage={listViewPage}
                  setListViewPage={setListViewPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  headers={visibleHeaders}
                />
              )}
            </Mui.Grid>
          </Paper>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
